import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Database,
  Globe2,
  KeyRound,
  Mic,
  Server,
  Wallet,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * /free — the offer page. One idea: free forever, for every gym.
 * Three clauses (the cost model, the surface, the self-host escape
 * hatch), one CTA. No pricing tiers — there are none.
 */
export const FreePage = () => {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('landingFreePage.seoTitle', {
      defaultValue: 'GymGamify — Free forever',
    });
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        t('landingFreePage.seoDescription', {
          defaultValue:
            'GymGamify is free forever, for every gym. No card, no trial, no upcharges. Open registration, every feature, every integration, unlimited members — and a self-host path if you outgrow the platform.',
        })
      );
    }
  }, [t]);

  const costClauses = [
    {
      icon: Wallet,
      head: 'No card. No trial. No catch.',
      body: 'Open registration. The whole product, all integrations, unlimited members, on day one. There is no "Pro" tier, no "Enterprise" tier, no paywall on the leaderboard or the analytics page.',
    },
    {
      icon: Database,
      head: 'Your data is yours.',
      body: 'Every gym owns its member data. Export in standard formats at any time. The same exports power the optional self-host path — your data leaves with you if you go.',
    },
    {
      icon: Server,
      head: 'Self-host when you outgrow us.',
      body: 'Run GymGamify on your own infrastructure. Same codebase, same Keycloak OIDC, same Postgres / Mongo back end. EU single-tenant hosting is also available if you want managed infra in your region.',
    },
  ];

  return (
    <div className="layout-shell py-14 lg:py-20">
      <section className="hero-full-bleed min-h-0">
        <div className="hero-media-plane" />
        <div className="relative z-10 motion-enter rounded-[2rem] border border-[var(--color-border)] bg-[rgba(6,12,10,0.66)] p-8 md:p-12 lg:p-16">
          <span className="hero-kicker">
            {t('landingFreePage.kicker', { defaultValue: 'Free forever' })}
          </span>
          <h1 className="mt-6 editorial-title max-w-3xl text-[var(--color-text)]">
            {t('landingFreePage.title', {
              defaultValue: 'Free forever, for every gym.',
            })}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-[var(--color-text-muted)]">
            {t('landingFreePage.subtitle', {
              defaultValue:
                'No card, no trial, no upcharges. Open registration, every feature, every integration, unlimited members. If the platform ever stops fitting, your data exports and a self-host path come with it.',
            })}
          </p>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link to="/register" className="btn btn-primary whitespace-nowrap">
              {t('landingFreePage.primaryCta', {
                defaultValue: 'Get started — create your account',
              })}
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/features"
              className="btn whitespace-nowrap border border-[var(--color-border)] bg-white/[0.03] text-[var(--color-text)] hover:border-[var(--color-text-muted)]"
            >
              {t('landingFreePage.secondaryCta', {
                defaultValue: 'See what is in it',
              })}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Three clauses that prove "free" actually means free. */}
      <section className="mt-14 space-y-5">
        {costClauses.map((c) => (
          <article key={c.head} className="editorial-panel p-8 md:p-10">
            <div className="flex items-start gap-5">
              <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-primary-200/15 bg-primary-200/10">
                <c.icon className="h-4 w-4 text-primary-100" />
              </span>
              <div>
                <h2 className="font-display text-2xl uppercase tracking-[0.04em] text-[var(--color-text)] md:text-3xl">
                  {c.head}
                </h2>
                <p className="mt-3 max-w-3xl text-base leading-8 text-[var(--color-text-muted)]">
                  {c.body}
                </p>
              </div>
            </div>
          </article>
        ))}
      </section>

      {/* What you get on day one — concrete list, no marketing. */}
      <section className="mt-16">
        <div className="editorial-panel p-8 md:p-12">
          <div className="grid gap-10 md:grid-cols-[1fr_1.1fr] md:items-start">
            <div>
              <span className="hero-kicker">On day one</span>
              <h2 className="mt-5 editorial-title text-[var(--color-text)]">
                Everything is on. Nothing is gated.
              </h2>
              <p className="mt-5 max-w-md text-base leading-8 text-[var(--color-text-muted)]">
                Free means the whole product. The same modules we sell
                operators on, the same features trainers use day to day.
                No comparison table, no asterisks.
              </p>
            </div>
            <ul className="grid gap-2.5 text-sm text-[var(--color-text-muted)] sm:grid-cols-2">
              {[
                'Workout logging with the 3500+ exercise catalogue',
                'Self-logged or trainer-issued sessions',
                'Points, levels, daily/weekly caps',
                'Configurable badges with bonus points',
                'Live role-aware leaderboards',
                'Team & individual challenges',
                'Rewards catalog with redemption flow',
                'Social feed + 1-to-1 direct messages',
                'Teams with team leaderboards',
                'Insights — muscle map, calendar, PR progression',
                'Tamper-proof hash-chained ledger + integrity page',
                'Suspicious activity flagging + admin review',
                'Keycloak OIDC, role-based access control',
                'Standard data exports + self-host path',
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary-200" />
                  <span className="leading-6">{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* "Why we do this" — short, not a manifesto. Three icons. */}
      <section className="mt-16">
        <div className="grid gap-4 md:grid-cols-3">
          <article className="editorial-panel motion-hover-lift p-7">
            <Globe2 className="h-5 w-5 text-primary-200" />
            <h2 className="mt-5 font-display text-xl uppercase tracking-[0.04em] text-[var(--color-text)]">
              More gyms, more signal
            </h2>
            <p className="mt-3 text-sm leading-7 text-[var(--color-text-muted)]">
              A free product gets used. A used product surfaces patterns we
              would not see in a paid niche. The retention loops get
              better when the floor is full.
            </p>
          </article>
          <article className="editorial-panel motion-hover-lift p-7">
            <KeyRound className="h-5 w-5 text-primary-200" />
            <h2 className="mt-5 font-display text-xl uppercase tracking-[0.04em] text-[var(--color-text)]">
              You stay because it works
            </h2>
            <p className="mt-3 text-sm leading-7 text-[var(--color-text-muted)]">
              No lock-in. The lock-in would come from the ledger and the
              streak history you have built, and that is the lock-in we
              want — earned, not extracted.
            </p>
          </article>
          <article className="editorial-panel motion-hover-lift p-7">
            <Mic className="h-5 w-5 text-primary-200" />
            <h2 className="mt-5 font-display text-xl uppercase tracking-[0.04em] text-[var(--color-text)]">
              We listen to operators
            </h2>
            <p className="mt-3 text-sm leading-7 text-[var(--color-text-muted)]">
              The roadmap is shaped by what gym owners and trainers tell
              us, in their own words, in the contact form and in the
              product. Free gets us more of that signal, not less.
            </p>
          </article>
        </div>
      </section>

      {/* Final CTA — same voice as the homepage, single sentence. */}
      <section className="mt-16">
        <div className="flex flex-col items-start gap-6 border-t border-[var(--color-border)] pt-12 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)]">
              No card required
            </p>
            <p className="mt-3 max-w-xl font-display text-3xl uppercase leading-tight text-[var(--color-text)]">
              Open an account, log a session, see the streak.
            </p>
          </div>
          <Link to="/register" className="btn btn-primary whitespace-nowrap">
            Create your account
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default FreePage;
